import { getStartOfWeek } from "../../components/formatted/FormattedDateAndTime";
import { addMonths } from "date-fns";

export function buildDefaultCampaignPeriod(now = new Date()) {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);

  const stop = addMonths(start, 1);
  stop.setHours(23, 59, 59, 999);

  return {
    start: start.toISOString(),
    stop: stop.toISOString(),
  };
}

function resolveObjectId(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value._id) return String(value._id);
  return String(value);
}

function resolveContainerIds(source) {
  if (source.containerObjectIdList?.length) {
    return source.containerObjectIdList.map(resolveObjectId).filter(Boolean);
  }
  if (source.containers?.length) {
    return source.containers.map((c) => resolveObjectId(c._id)).filter(Boolean);
  }
  return [];
}

/** Prépare les données d'une campagne dupliquée avec des dates renouvelées. */
export function buildDuplicateCampaign(source) {
  const originalStart = new Date(source.start);
  const originalStop = new Date(source.stop);
  const durationMs = Math.max(
    originalStop.getTime() - originalStart.getTime(),
    24 * 60 * 60 * 1000,
  );

  const newStart = getStartOfWeek(new Date());
  newStart.setHours(0, 0, 0, 0);
  const newStop = new Date(newStart.getTime() + durationMs);
  newStop.setHours(23, 59, 59, 999);

  return {
    start: newStart.toISOString(),
    stop: newStop.toISOString(),
    addressId: resolveObjectId(source.addressId || source.address?._id),
    trackedMaterials: [...(source.trackedMaterials || [])],
    containerObjectIdList: resolveContainerIds(source),
  };
}

export function toCampaignFormModel(source) {
  return {
    ...source,
    addressId: resolveObjectId(source.addressId || source.address?._id),
    containerObjectIdList: resolveContainerIds(source),
  };
}

const normalizeLabel = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/**
 * Prefill create-campaign form from a flux label (Bilans → campaign CTA).
 * Matches containers by wasteName, then picks the address with most matches.
 */
export function buildCampaignPrefillFromFlux({
  fluxLabel,
  trackedMaterial,
  containerList = [],
  addressList = [],
}) {
  const { start, stop } = buildDefaultCampaignPeriod();

  const empty = {
    start,
    stop,
    addressId: addressList[0]?._id || "",
    containerObjectIdList: [],
    trackedMaterials: trackedMaterial ? [trackedMaterial] : [],
  };

  const target = normalizeLabel(fluxLabel);
  if (!target || !containerList.length) {
    return empty;
  }

  const authorizedAddressIds = new Set(
    addressList.map((address) => String(address._id)),
  );

  const matchingContainers = containerList.filter((container) => {
    const addressId = container.addressId?.toString?.() ?? "";
    if (authorizedAddressIds.size > 0 && !authorizedAddressIds.has(addressId)) {
      return false;
    }
    const wasteName = normalizeLabel(container.wasteName);
    if (!wasteName) return false;
    return (
      wasteName === target ||
      wasteName.includes(target) ||
      target.includes(wasteName)
    );
  });

  const exactMatches = matchingContainers.filter(
    (container) => normalizeLabel(container.wasteName) === target,
  );
  const candidates =
    exactMatches.length > 0 ? exactMatches : matchingContainers;

  const countByAddress = {};
  candidates.forEach((container) => {
    const addressId = container.addressId?.toString?.();
    if (!addressId) return;
    countByAddress[addressId] = (countByAddress[addressId] || 0) + 1;
  });

  const bestAddressId =
    Object.entries(countByAddress).sort((a, b) => b[1] - a[1])[0]?.[0] ||
    addressList[0]?._id ||
    "";

  const containerObjectIdList = candidates
    .filter(
      (container) =>
        container.addressId?.toString?.() === String(bestAddressId),
    )
    .map((container) => container._id)
    .filter(Boolean);

  return {
    start,
    stop,
    addressId: bestAddressId,
    containerObjectIdList,
    trackedMaterials: trackedMaterial ? [trackedMaterial] : [],
  };
}
